import React, { useCallback, useContext } from "react";
import { connect } from "react-redux";

import { FormikValues, Formik } from "formik";
import { Dispatch, bindActionCreators } from "redux";

import { Dialog, Form, CancelLink, OkButton, TextArea, ValidationMessage } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { useLocalizedValues, useLocalizer } from "ui/hooks";
import commonStyles from "ui/screens/translation-managers/shared/styles/Translations.module.scss";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { plexTranslationMessagesActions } from "../PlexTranslationMessages.actions";
import { IPlexTranslationMessagesManagerState } from "../PlexTranslationMessages.state";

import styles from "./CommentPlexTranslationDialog.module.scss";

export interface ICommentDialog {
  dialogTitle: string;
  notifications: INotification[];
  comment?: string;
  changeCommentText: (comment: string) => void;
  submitComment?: () => void;
  setValidationBanner: () => void;
  clearNotificationsActionBar: () => void;
}

const CommentPlexTranslationDialog: React.FC<ICommentDialog> = ({
  dialogTitle,
  notifications,
  comment,
  changeCommentText,
  submitComment,
  setValidationBanner,
  clearNotificationsActionBar
}) => {
  const localizer = useLocalizer();

  const localizedValues = useLocalizedValues(() => {
    return {
      comment: displayMessages.property("comment")
    };
  });

  const submitCommentExecute = useCallback(() => {
    submitComment?.();
  }, [submitComment]);

  const dialogNavigationContext = useContext(DialogNavigationContext);

  const onTextChange = useCallback(
    (value: string | null) => {
      changeCommentText(value ? value : "");
    },
    [changeCommentText]
  );

  const close = useCallback(() => {
    clearNotificationsActionBar();
    dialogNavigationContext.hideDialog();
  }, [clearNotificationsActionBar, dialogNavigationContext]);

  const cancel = useCallback(() => {
    changeCommentText("");
    close();
  }, [close, changeCommentText]);

  return (
    <DialogContainer
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={commonStyles.dialogBanner} />
              <Formik
                initialValues={{
                  commentIdText: undefined
                }}
                validate={values => {
                  const errors: any = {};
                  if (!values.commentIdText) {
                    errors.commentIdText = localizer(validationMessages.error("propertyIsRequired"), {
                      property: localizedValues.comment
                    });
                    setValidationBanner();
                  } else {
                    clearNotificationsActionBar();
                  }

                  return errors;
                }}
                onSubmit={(_: FormikValues, { setSubmitting }) => {
                  submitCommentExecute();
                  setSubmitting(false);
                  close();
                }}
                validateOnBlur={false}
                isInitialValid={false}
              >
                {({ errors, handleBlur, handleSubmit, handleChange }) => (
                  <Form>
                    <Form.Body>
                      <Form.Section title={localizedValues.comment}>
                        <Form.Row>
                          <Form.FieldPair labelText={localizedValues.comment} required className={styles.formFieldPairComment} id="commentId">
                            <TextArea
                              key="commentKey"
                              id="commentIdText"
                              onBlur={handleBlur}
                              onChange={(text, e) => {
                                onTextChange(text);
                                handleChange(e);
                              }}
                              value={comment}
                            />
                            <ValidationMessage message={errors.commentIdText ? (errors.commentIdText as string) : ""} />
                          </Form.FieldPair>
                        </Form.Row>
                      </Form.Section>
                    </Form.Body>
                    <Form.Footer>
                      <CancelLink onClick={cancel}></CancelLink>
                      <OkButton onClick={() => handleSubmit()} />
                    </Form.Footer>
                  </Form>
                )}
              </Formik>
            </Dialog.Body>
          </Dialog>
        );
      }}
      onHidden={() => {
        clearNotificationsActionBar();
        changeCommentText("");
      }}
    />
  );
};

export default connect(
  (state: { plexTranslationMessagesManager: IPlexTranslationMessagesManagerState }) => {
    const actionBar = state.plexTranslationMessagesManager.actionBar;
    return {
      notifications: actionBar.notifications,
      comment: actionBar.review?.comment
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        changeCommentText: plexTranslationMessagesActions.changeCommentText,
        setValidationBanner: plexTranslationMessagesActions.setValidationBanner,
        clearNotificationsActionBar: plexTranslationMessagesActions.clearNotificationsActionBar
      },
      dispatch
    );
  }
)(CommentPlexTranslationDialog);
